<script setup lang="ts">
    import { computed, ref, onMounted } from 'vue';
    import { api } from '@cwd/lib/main';
    import { type SegmentProperty, type DynamicSubjectRow, NULLARY_OPERATORS, OPERATOR_RESOURCE_KEYS, deserialize } from './dynamicSubject';

    const props = defineProps<{
        dynamicSubject: string | null;
        categoryName: string;
        onSubjectClick: ((rows: DynamicSubjectRow[], negate: boolean, label: string) => void) | undefined;
    }>();

    const segmentProperties = ref<SegmentProperty[]>([]);

    onMounted(async () => {
        if (props.dynamicSubject) {
            segmentProperties.value = (await api.get('/Segments/Properties')) as SegmentProperty[];
        }
    });

    function getPropertyLabel(prop: string): string {
        const name = prop.replace(/^Contact\./, '');
        return segmentProperties.value.find((p) => p.Name === name)?.Label ?? name;
    }

    const readableParsed = computed(() => {
        if (!props.dynamicSubject) return null;
        const { rows, defaultSubject } = deserialize(props.dynamicSubject);
        if (rows.length === 0) return null;
        return { rows, defaultSubject };
    });
</script>

<template>
    <h1 class="h4 m-0-t m-xs-b">{{ $Resources.DynamicSubjectHeading }}:</h1>

    <ul class="list-unstyled dynamic-subject-list" v-if="readableParsed">
        <li class="d-flex align-items-center col-gap-lg" v-for="(row, i) in readableParsed.rows" :key="i">
            <h2 class="h3 m-0-all">
                <small class="d-block">
                    {{ getPropertyLabel(row.property) }}
                    {{ $Resources[OPERATOR_RESOURCE_KEYS[row.operator]] }}
                    <template v-if="!NULLARY_OPERATORS.has(row.operator)">{{ '"' + row.value + '": ' }}</template>
                </small>
                {{ row.subject }}
            </h2>

            <button v-if="onSubjectClick" type="button" class="btn btn-default btn-xs m-auto-l" @click="onSubjectClick([row], false, row.subject)">
                {{ $Resources.FilterAddFilterBtnText }}
            </button>
        </li>

        <li class="d-flex align-items-center col-gap-lg">
            <h2 class="h3 m-0-all">
                <small class="d-block">{{ $Resources.ElseLabel }}:</small>
                {{ readableParsed.defaultSubject }}
            </h2>

            <button
                v-if="onSubjectClick"
                type="button"
                class="btn btn-default btn-xs m-auto-l"
                @click="onSubjectClick(readableParsed.rows, true, readableParsed.defaultSubject)">
                {{ $Resources.FilterAddFilterBtnText }}
            </button>
        </li>
    </ul>

    <p>
        <span class="fw-600 m-xs-r">{{ $Resources.CategoryLabel }}:</span>
        {{ categoryName }}
    </p>
</template>

<style>
    .dynamic-subject-list > li {
        padding: var(--ug-spacing-md) var(--ug-spacing-lg);
        border-radius: var(--border-radius);
    }

    .dynamic-subject-list > li:nth-child(odd) {
        background-color: var(--brand-secondary-50);
    }
</style>
